import React, { useEffect, useContext, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin, { Draggable } from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import multiMonthPlugin from '@fullcalendar/multimonth';
import bootstrapPlugin from "@fullcalendar/bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import { AppSettings } from "./../../config/app-settings.js";
import { workOrder } from "../../api";
import { Card, CardBody, CardHeader } from "./../../components/card/card.jsx";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getStatusColor } from "../../utils/helper.jsx";
import { PermissionChecker } from "../../components/PermissionChecker/index.jsx";
import ConfirmationDlg from "../../components/ConfirmationDlg/index.jsx";
import { addDays, differenceInDays, endOfMonth, isAfter, isSameDay, startOfMonth } from "date-fns";
import rrulePlugin from "@fullcalendar/rrule";
import LoadingIndicator from "../../components/LoadingIndicator";
import "./calendar.css";

function CalendarWorkOrder({ query }) {
  const context = useContext(AppSettings);
  const plugins = [
    dayGridPlugin,
    multiMonthPlugin,
    timeGridPlugin,
    interactionPlugin,
    listPlugin,
    bootstrapPlugin,
    rrulePlugin,
  ];
  const headerToolbar = {
    left: "multiMonthYear,dayGridMonth,timeGridWeek,timeGridDay",
    center: "title",
    right: "prev,next today",
  };
  const buttonText = {
    today: "Today",
    month: "Month",
    year: "Year",
    week: "Week",
    day: "Day",
  };
  const views = {
    timeGrid: {
      eventLimit: 6,
    },
  };
  const [events, setEvents] = useState();
  const [deleteRow, setDeleteRow] = useState(null);
  const [openDetail, setOpenDetail] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [date, setDate] = useState({
    start: null,
    end: null
  })
  const [openOverlapDialog, setOpenOverlapDialog] = useState(false)
  const [overlapData, setOverlapData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [calendarLoading, setCalendarLoading] = useState(false)
  const navigate = useNavigate()
  const [openRemoveWorkOrderDialog, setOpenRemoveWorkOrderDialog] = useState(false)
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null)

  const [removeType, setRemoveType] = useState("single")

  async function deleteData() {
    try {
      const response = await workOrder.delete(deleteRow.id);
      if (response.ok) {
        const body = await response.json();
        if (body.success) {
          toast.success(
            `This ${deleteRow.is_request ? "request" : "work order"
            } has been deleted`
          );

          setDeleteRow(null);
          setSelectedEvent(null)
          await getMachine();
        } else {
          toast.error(body.message);
        }
      }
    } catch (e) {
      toast.error(e.message);
      console.error(e);
    }
  }

  async function getMachine() {
    try {
      setCalendarLoading(true)
      const queryParams = {
        status: query.status,
        keyword: query.keyword,
        category_id: query.category_id,
        location_id: query.location_id,
      }
      const res = await workOrder.list({ isRequest: false, ...date, calendar: true, ...queryParams });
      if (res.ok) {
        const body = await res.json();
        let datas = body.data;
        if (datas) {
          datas.map((item) => {
            item.allDay = true
            item.id = item.id
            item.title = item.title;
            item.start = new Date(item.start_date);
            item.end = addDays(new Date(item.start_date), 1);

            item.color = getComputedStyle(document.body)
              .getPropertyValue(`--bs-${getStatusColor(item.status)}`)
              .trim();;
          });

          setEvents(datas);
        }
      }
    } catch (e) {
      toast.error(e.message);
      console.error(e);
    }
    finally {
      setCalendarLoading(false)
    }
  }

  useEffect(() => {

    if (date.start) {
      getMachine();
    }

    return function cleanUp() {
      context.setAppContentFullHeight(false);
      context.setAppContentClass("");
    };

    // eslint-disable-next-line
  }, [date, query.status, query.keyword, query.category_id, query.location_id]);

  const handleEventClick = (e) => {
    setSelectedEvent(e.event)
    setOpenDetail(true)
  }

  const isAlreadyCreated = (e) => {
    return isSameDay(new Date(e.start), new Date(e.extendedProps.start_date))
  }

  const getOverlapingWorkOrder = async () => {
    setIsLoading(true)
    try {
      const res = await workOrder.getOverlapingWorkOrder()
      if (res.ok) {
        const body = await res.json();
        console.log(body.data)
        setOverlapData(body.data)
        setOpenOverlapDialog(true)
      } else {
        toast.error("Failed to fetch overlapping work orders")
      }
    } catch (error) {
      toast.error("Error fetching overlapping work orders: " + error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const handleRemoveWorkOrder = (workOrder) => {
    setSelectedWorkOrder(workOrder)
    setOpenRemoveWorkOrderDialog(true)
  }
  const submitRemoveWorkOrder = async () => {
    setOpenRemoveWorkOrderDialog(false)
    const overlappingWorkOrders = []
    if (removeType == "single") {
      overlappingWorkOrders.push(selectedWorkOrder)
    } else {

      overlapData.overlaps.forEach((data) => {
        if (data.workOrder1.recurring_template_id == selectedWorkOrder.recurring_template_id) { overlappingWorkOrders.push(data.workOrder1) }
        if (data.workOrder2.recurring_template_id == selectedWorkOrder.recurring_template_id) { overlappingWorkOrders.push(data.workOrder2) }

      })

    }

    try {
      setIsLoading(true)
      const res = await workOrder.removeOverlapingWorkOrder({ overlappingWorkOrders })
      if (res.ok) {
        toast.success("Work orders removed successfully")
        getOverlapingWorkOrder()
        getMachine()
      } else {
        toast.error("Failed to remove work orders")
      }
    } catch (error) {
      toast.error("Error removing work orders: " + error.message)
    } finally {
      setIsLoading(false)
    }

  }

  const handleEditWorkOrder = (workOrder) => {
    window.open('/work-order/edit/' + workOrder.id, '_blank')
  }

  return (
    <Card>
      <div className="py-4 d-flex w-100 justify-content-end">

        {['Open', 'In Progress', 'On Hold', 'Completed'].map((s) => {

          return <div key={s} style={{ marginRight: 10 }}>
            <span className={`bg-${getStatusColor(s)}`} style={{ width: '10px', height: 10, borderRadius: '50%', display: 'inline-table' }}> </span> {s}
          </div>
        })}

        <div>
          <button className="btn btn-danger btn-sm me-2" onClick={() => getOverlapingWorkOrder()}>Get Overlaping Work Order</button>

        </div>
      </div>

      <LoadingIndicator open={isLoading} />

      <div style={{ paddingLeft: '10px' }}>

        {calendarLoading && (
          <div
            className="d-flex justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100"
            style={{
              height: "100%",
              width: "100%",
              backgroundColor: "rgba(220, 219, 219, 0.2)",
              zIndex: 1000,
            }}
          >
            <div className="spinner-grow text-theme"></div>
          </div>
        )}


        <FullCalendar
          datesSet={(e) => setDate({ start: e.start, end: e.end })}
          eventClick={(e) => handleEventClick(e)}
          plugins={plugins}
          initialView="dayGridMonth"
          themeSystem="bootstrap"
          headerToolbar={headerToolbar}
          buttonText={buttonText}
          events={events}
          views={views}
          dayMaxEvents={5}
        />
      </div>

      {selectedEvent && <ConfirmationDlg open={openDetail}
        onClose={() => setOpenDetail(false)}
        btnSubmitText={'Show Detail'}
        hideSubmitBtn={true}
        onSubmit={() => navigate('/work-order/detail/' + selectedEvent?.id)}
        hideCancelBtn
      ><Card>

          <CardBody>
            <table>
              <tbody>
                <tr>
                  <td>No</td>
                  <td>:</td>
                  <td>WO-{selectedEvent.extendedProps?.number}</td>
                </tr>

                <tr>
                  <td>Title</td>
                  <td>:</td>
                  <td>{selectedEvent.title}</td>
                </tr>
                <tr>
                  <td>Asset</td>
                  <td>:</td>
                  <td>{selectedEvent.extendedProps?.asset?.asset_name}</td>
                </tr>
                <tr>
                  <td>Category</td>
                  <td>:</td>
                  <td>{selectedEvent.extendedProps?.category?.name}</td>
                </tr>
                <tr>
                  <td>Description</td>
                  <td>:</td>
                  <td>{selectedEvent.extendedProps?.description}</td>
                </tr>
                <tr>
                  <td>Priority</td>
                  <td>:</td>
                  <td>{selectedEvent.extendedProps?.priority}</td>
                </tr>
                <tr>
                  <td>Status</td>
                  <td>:</td>
                  <td>  <span
                    className={`badge border border-${getStatusColor(selectedEvent.extendedProps?.status)
                      } text-${getStatusColor(selectedEvent.extendedProps?.status)}`}
                  >
                    {selectedEvent.extendedProps?.status}
                  </span></td>
                </tr>
              </tbody>
            </table>
          </CardBody>

        </Card>
        <Card className="mt-3">
          <CardHeader>
            Action
          </CardHeader>
          <CardBody className="text-center">

            <button
              className="btn btn-outline-theme m-2"
              onClick={() => navigate('/work-order/detail/' + selectedEvent?.id)}
            >
              <i className="fa fa-eye"></i> View Detail
            </button>
            <button
              className="btn btn-primary m-2"
              onClick={() => navigate('/work-order/edit/' + selectedEvent?.id)}
            >
              <i className="fa fa-edit"></i> Edit WO
            </button>

            <PermissionChecker permissions={["delete work order"]} hideIfNotAllowed>
              <button
                onClick={() => setDeleteRow(selectedEvent)}
                type="button"
                className="btn btn-outline-danger m-2"
              >
                <i className="fa fa-edit"></i> Delete WO
              </button>
            </PermissionChecker>
          </CardBody>
        </Card>

      </ConfirmationDlg>}

      {/* Overlap Dialog */}
      {overlapData && <ConfirmationDlg
        open={openOverlapDialog}
        onClose={() => setOpenOverlapDialog(false)}
        title="Work Order Overlaps Detected"
        btnSubmitText={'Close'}
        onSubmit={() => setOpenOverlapDialog(false)}
        hideCancelBtn
        size="xl"
      >
        <Card>
          <CardHeader className="bg-warning text-dark">
            <h5 className="mb-0">
              <i className="fa fa-exclamation-triangle me-2"></i>
              {overlapData.total_overlaps} Overlapping Work Orders Found for {overlapData.year}
            </h5>
          </CardHeader>
          <CardBody style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {overlapData.overlaps.map((overlap, index) => (
              <div key={index} className="p-3 mb-3">
                <div className="row">
                  <div className="col-md-5">
                    <Card>
                      <CardHeader className="bg-primary text-white py-2">
                        <h6 className="mb-0">
                          Work Order 1
                          {overlap.workOrder1.is_future && <span className="badge bg-warning ms-2">Future</span>}
                        </h6>
                      </CardHeader>
                      <CardBody className="py-2">
                        <table className="table table-sm mb-0">
                          <tbody>
                            <tr>
                              <td><strong>No:</strong></td>
                              <td>WO-{overlap.workOrder1.number}</td>
                            </tr>
                            <tr>
                              <td><strong>Title:</strong></td>
                              <td>{overlap.workOrder1.title}</td>
                            </tr>
                            <tr>
                              <td><strong>Asset:</strong></td>
                              <td>{overlap.workOrder1.asset_name}</td>
                            </tr>
                            <tr>
                              <td><strong>Category:</strong></td>
                              <td>{overlap.workOrder1.category_name}</td>
                            </tr>
                            <tr>
                              <td><strong>Start Date:</strong></td>
                              <td>{formatDate(overlap.workOrder1.start_date)}</td>
                            </tr>
                            <tr>
                              <td><strong>Due Date:</strong></td>
                              <td>{formatDate(overlap.workOrder1.due_date)}</td>
                            </tr>
                            <tr>
                              <td><strong>Description:</strong></td>
                              <td>{overlap.workOrder1.description}</td>
                            </tr>

                            <tr>
                              <td><strong>Recurring type:</strong></td>
                              <td>
                                <span className="badge bg-info">{overlap.workOrder1.recurring_wo || 'One Time'}</span>
                              </td>
                            </tr>

                            <tr>
                              <td colSpan={2} className="p-2">
                                <button className="btn btn-warning btn-sm w-100" onClick={() => handleEditWorkOrder(overlap.workOrder1)}>Edit Work Order</button>
                              </td>
                            </tr>

                            <tr>
                              <td colSpan={2} className="p-2">
                                <button className="btn btn-danger btn-sm w-100" onClick={() => handleRemoveWorkOrder(overlap.workOrder1)}>Remove Work Order</button>
                              </td>
                            </tr>


                          </tbody>
                        </table>
                      </CardBody>
                    </Card>
                  </div>

                  <div className="col-md-2 d-flex align-items-center justify-content-center">
                    <div className="text-center">
                      <i className="fa fa-exchange-alt fa-2x text-danger"></i>
                      <div className="mt-2">
                        <small className="text-muted">Overlaps</small>
                        <br />
                        <small className="text-danger">
                          {formatDate(overlap.overlap_start)} - {formatDate(overlap.overlap_end)}
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-5">
                    <Card>
                      <CardHeader className="bg-secondary text-white py-2">
                        <h6 className="mb-0">
                          Work Order 2
                          {overlap.workOrder2.is_future && <span className="badge bg-warning ms-2">Future</span>}
                        </h6>
                      </CardHeader>
                      <CardBody className="py-2">
                        <table className="table table-sm mb-0">
                          <tbody> <tr>
                            <td><strong>No:</strong></td>
                            <td>WO-{overlap.workOrder2.number}</td>
                          </tr>
                            <tr>
                              <td><strong>Title:</strong></td>
                              <td>{overlap.workOrder2.title}</td>
                            </tr>
                            <tr>
                              <td><strong>Asset:</strong></td>
                              <td>{overlap.workOrder2.asset_name}</td>
                            </tr>
                            <tr>
                              <td><strong>Category:</strong></td>
                              <td>{overlap.workOrder2.category_name}</td>
                            </tr>
                            <tr>
                              <td><strong>Start Date:</strong></td>
                              <td>{formatDate(overlap.workOrder2.start_date)}</td>
                            </tr>
                            <tr>
                              <td><strong>Due Date:</strong></td>
                              <td>{formatDate(overlap.workOrder2.due_date)}</td>
                            </tr>
                            <tr>
                              <td><strong>Description:</strong></td>
                              <td>{overlap.workOrder2.description}</td>
                            </tr>
                            <tr>
                              <td><strong>Recurring type:</strong></td>
                              <td>
                                <span className="badge bg-info">{overlap.workOrder2.recurring_wo || 'One Time'}</span>
                              </td>
                            </tr>

                            <tr>
                              <td colSpan={2} className="p-2">
                                <button className="btn btn-warning btn-sm w-100" onClick={() => handleEditWorkOrder(overlap.workOrder2)}>Edit Work Order</button>
                              </td>
                            </tr>

                            <tr>
                              <td colSpan={2} className="p-2">
                                <button className="btn btn-danger btn-sm w-100" onClick={() => handleRemoveWorkOrder(overlap.workOrder2)}>Remove Work Order</button>
                              </td>
                            </tr>


                          </tbody>
                        </table>
                      </CardBody>
                    </Card>
                  </div>
                </div>

                {index < overlapData.overlaps.length - 1 && <hr className="my-3" />}
              </div>
            ))}

            {overlapData.total_overlaps === 0 && (
              <div className="text-center py-4">
                <i className="fa fa-check-circle fa-3x text-success mb-3"></i>
                <h5 className="text-success">No Overlapping Work Orders Found</h5>
                <p className="text-muted">All work orders for {overlapData.year} are properly scheduled without conflicts.</p>
              </div>
            )}
          </CardBody>
        </Card>
      </ConfirmationDlg>}


      <ConfirmationDlg
        open={openRemoveWorkOrderDialog}
        onClose={() => setOpenRemoveWorkOrderDialog(false)}
        title="Remove Work Order"
        btnSubmitText={'Remove'}
        onSubmit={() => submitRemoveWorkOrder()}
        hideCancelBtn
        type="confirmation"
      >

        <p>Are you sure you want to remove "<strong>{selectedWorkOrder?.title}</strong>" work order?</p>

        {
          selectedWorkOrder?.recurring_template_id && (<><div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="flexRadioDefault"
              id="flexRadioDefault1"
              checked={removeType == "single"}
              onChange={() => setRemoveType("single")}
            />
            <label class="form-check-label" for="flexRadioDefault1">
              Only this work order
            </label>
          </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                name="flexRadioDefault"
                id="flexRadioDefault2"
                checked={removeType == "all"}
                onChange={() => setRemoveType("all")}
              />
              <label class="form-check-label" for="flexRadioDefault2">
                All overlapping work orders onwards
              </label>
            </div></>)
        }


      </ConfirmationDlg>

      {deleteRow && (
        <ConfirmationDlg
          open={!!deleteRow}
          onClose={() => setDeleteRow(null)}
          onSubmit={deleteData}
          title={`Delete Work Order`}
          submitText="Delete"
        >
          Are you sure you want to delete this work order{" "}
          <strong>{deleteRow.title}</strong> ?
        </ConfirmationDlg>
      )}

    </Card>

  );
}

export default CalendarWorkOrder;
